import { useState } from 'react';
import { FormattedMessage } from 'react-intl';

import { ExclamationIcon } from '@/components/icons/exclamation';
import { IneligibleOpeningReason } from '@/components/pages/job-posting/types';
import { Button } from '@/components/ui/button';
import { Typography } from '@/components/ui/typography';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { listingStateMessages } from '../posting-unavailable-messages';
import { useAuthContext } from '@/contexts/auth-context';

export const MAX_INELIGIBLE_OPENINGS_COUNT = 3;

function getReasonLabel(reason: IneligibleOpeningReason) {
  if (reason in listingStateMessages) {
    return (
      <FormattedMessage
        defaultMessage="({state})"
        description="Tag suffix: the listing state that blocks posting; {state} is Internal, Not posted or Intake closed"
        values={{
          state: (
            <FormattedMessage
              {...listingStateMessages[reason as keyof typeof listingStateMessages]}
            />
          ),
        }}
      />
    );
  }
  switch (reason) {
    case 'inactive':
      return (
        <FormattedMessage
          defaultMessage="(Inactive)"
          description="Tag suffix: opening is not active in Hire"
        />
      );
    case 'sourcing_disabled':
      return (
        <FormattedMessage
          defaultMessage="(Sourcing Disabled)"
          description="Tag suffix: sourcing funnel is off for that job"
        />
      );
    default:
      return null;
  }
}

/**
 * List of ineligible openings shown in the openings panel
 */
export function IneligibleOpenings() {
  const { ineligibleOpenings } = useJobPostingContext();
  const { whoami } = useAuthContext();

  const [isExpanded, setIsExpanded] = useState(false);

  // If there are no ineligible openings, return null
  if (ineligibleOpenings.length === 0) return null;

  const visibleOpenings = isExpanded
    ? ineligibleOpenings
    : ineligibleOpenings.slice(0, MAX_INELIGIBLE_OPENINGS_COUNT);
  const hasMore =
    ineligibleOpenings.length > MAX_INELIGIBLE_OPENINGS_COUNT && !isExpanded;

  return (
    <div className="bg-fx-rose-50 m-3 flex items-center gap-2 self-stretch rounded-md p-2">
      <div className="flex grow flex-col items-start justify-center">
        <div className="flex flex-col gap-2">
          <div className="flex items-start gap-2">
            <ExclamationIcon className="text-fx-rose mt-0.5 shrink-0" />
            <Typography variant="body1">
              <FormattedMessage
                defaultMessage="{count, plural, one {# selected opening isn't eligible for posting and will be excluded.} other {# selected openings aren't eligible for posting and will be excluded.}}"
                values={{ count: ineligibleOpenings.length }}
                description="Summary line; {count} controls singular vs plural in ICU plural"
              />
            </Typography>
          </div>
          <div className="pl-7">
            <ul className="ml-4 list-disc">
              {visibleOpenings.map(opening => (
                <li key={opening.externalId} className="leading-5">
                  <Typography variant="body1">
                    {opening.title}{' '}
                    {getReasonLabel(
                      whoami?.account_listing_status_active !== true &&
                        opening.reason in listingStateMessages
                        ? 'sourcing_disabled'
                        : opening.reason,
                    )}
                  </Typography>
                </li>
              ))}
              {hasMore && (
                <Button
                  variant="text"
                  className="decoration-fx-rose"
                  onClick={() => setIsExpanded(true)}
                >
                  <Typography variant="body1" className="text-fx-rose">
                    <FormattedMessage
                      defaultMessage="View more"
                      description="Expand list of ineligible openings past the first few"
                    />
                  </Typography>
                </Button>
              )}
              {isExpanded &&
                ineligibleOpenings.length > MAX_INELIGIBLE_OPENINGS_COUNT && (
                  <Button
                    variant="text"
                    className="decoration-fx-rose"
                    onClick={() => setIsExpanded(false)}
                  >
                    <Typography variant="body1" className="text-fx-rose">
                      <FormattedMessage
                        defaultMessage="View less"
                        description="Collapse long list of ineligible openings"
                      />
                    </Typography>
                  </Button>
                )}
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
